import React, { useEffect, useRef, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { formatInZone } from "../../../utils/time";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import {
	fetchCustomers,
} from "../../../api/inventory/customerApi";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { useNotifications } from "../../../context/NotificationContext";
import CustomerDrawer from "./CustomerDrawer";
import CreateCustomerModal from "./CreateCustomerModal";
import { customerMoney } from "../../../types/inventory/customerRecord";
import { Customer } from "../../../types/inventory/CustomerTypes";

const displayName = (customer: Customer): string =>
	[
		customer.first_name,
		customer.middle_name,
		customer.last_name,
	]
		.filter((value) => Boolean(value && value.trim()))
		.join(" ")
		|| "—";

const CustomersPage: React.FC = () => {
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	// CU-86d44j2ac: active members by default; the toggle shows the deactivated ones.
	const [showDeactivated, setShowDeactivated] = useState(false);
	const [loading, setLoading] = useState(true);
	const [selectedCustomer, setSelectedCustomer] =
		useState<Customer | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const loadRequestIdRef = useRef(0);

	const pageSize = loadUserPreferences().tableRowsPerPage;
	const { showError } = useNotifications();

	const loadCustomers = async (
		search: string,
		page: number,
		deactivated = showDeactivated,
	): Promise<{ data: Customer[]; total: number } | null> => {
		const requestId = ++loadRequestIdRef.current;

		setLoading(true);

		try {
			const response = await fetchCustomers(page, pageSize, search, !deactivated);

			if (requestId !== loadRequestIdRef.current) return null;

			setCustomers(response.data);
			setTotal(response.total);
			setCurrentPage(page);

			// CU-86d44j2ac: keep an open drawer on the freshly loaded row (the drawer's Refresh re-reads it).
			setSelectedCustomer((current) => {
				if (!current) return current;

				const fresh = response.data.find(
					(candidate: Customer) => candidate.id === current.id,
				);

				return fresh ? { ...current, ...fresh } : current;
			});

			return { data: response.data, total: response.total };
		} catch (error) {
			if (requestId !== loadRequestIdRef.current) return null;

			console.error("Error fetching customers:", error);
			showError("Failed to load customers.");

			return null;
		} finally {
			if (requestId === loadRequestIdRef.current) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		void loadCustomers("", 1);

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	const handleSearch = (value: string) => {
		setSearchTerm(value);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		searchTimeoutRef.current = setTimeout(() => {
			void loadCustomers(value, 1);
		}, 300);
	};

	const handleToggleDeactivated = () => {
		const next = !showDeactivated;
		setShowDeactivated(next);
		void loadCustomers(searchTerm, 1, next);
	};

	const handleReset = () => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
			searchTimeoutRef.current = null;
		}

		setSearchTerm("");
		setShowDeactivated(false);
		void loadCustomers("", 1, false);
	};

	const handlePageChange = (page: number) => {
		void loadCustomers(searchTerm, page);
	};

	const openCustomer = (customer: Customer) => {
		setSelectedCustomer(customer);
		setDrawerOpen(true);
	};

	const handleCustomerSaved = (updated: Customer) => {
		setSelectedCustomer(updated);
		setCustomers((current) =>
			current.map((customer) =>
				customer.id === updated.id
					? { ...customer, ...updated }
					: customer
			)
		);
	};

	// CU-86d44j2ac: an activation change reloads the current filtered page, so the row leaves a view whose
	// filter no longer includes it and the total stays authoritative; an emptied page falls back to the last one.
	const handleCustomerActivationChanged = async (updated: Customer) => {
		setSelectedCustomer(updated);

		const reloaded = await loadCustomers(searchTerm, currentPage);

		if (reloaded && reloaded.data.length === 0 && reloaded.total > 0 && currentPage > 1) {
			await loadCustomers(searchTerm, Math.max(1, Math.ceil(reloaded.total / pageSize)));
		}
	};

	const columns: ColumnsType<Customer> = [
		{
			title: "Customer",
			key: "customer",
			render: (_, customer) => displayName(customer),
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			render: (value: string | null) => value || "—",
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			ellipsis: true,
		},
		{
			title: "Orders",
			dataIndex: "orders",
			key: "orders",
			width: 100,
			render: (value: number) => value ?? 0,
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			width: 130,
			render: (value: number) => customerMoney.format(Number(value || 0)),
		},
		{
			// CU-86d44j2ac: the CURRENT account type — never the durable membership flag.
			title: "User Type",
			dataIndex: "user_type",
			key: "user_type",
			width: 120,
			render: (value: string | null) => value || "—",
		},
		{
			title: "Joined",
			dataIndex: "created_at",
			key: "created_at",
			width: 130,
			render: (value: string) =>
				value ? formatInZone(value, "DD MMM YYYY") || "—" : "—",
		},
	];

	return (
		<Layout>
			<PageContainer>
				<PageToolbar
					primaryAction={{
						label: "Create Customer",
						onClick: () => setCreateOpen(true),
						testId: "customers-create-btn",
					}}
					extraActions={[
						{
							label: showDeactivated ? "Hide Deactivated" : "Show Deactivated",
							icon: <EyeOutlined />,
							onClick: handleToggleDeactivated,
						},
					]}
					filters={[
						{
							type: "search",
							placeholder: "Search customers...",
							value: searchTerm,
							onChange: handleSearch,
							width: 320,
						},
					]}
					onReset={handleReset}
				/>

				<DataTable
					columns={columns}
					dataSource={customers}
					rowKey="id"
					loading={loading}
					onRowClick={openCustomer}
					total={total}
					currentPage={currentPage}
					pageSize={pageSize}
					onPageChange={handlePageChange}
					emptyTitle="No customers found"
					emptyDescription={
						showDeactivated
							? "No deactivated customers match this view."
							: "No customers match this view."
					}
					fillHeight
				/>

				<CustomerDrawer
					open={drawerOpen}
					customer={selectedCustomer}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedCustomer(null);
					}}
					onSaved={handleCustomerSaved}
					onActivationChanged={handleCustomerActivationChanged}
					onRefresh={async () => (await loadCustomers(searchTerm, currentPage)) !== null}
					refreshing={loading}
				/>

				<CreateCustomerModal
					open={createOpen}
					onClose={() => setCreateOpen(false)}
					onCreated={() => {
						setSearchTerm("");
						setShowDeactivated(false);
						void loadCustomers("", 1, false);
					}}
				/>
			</PageContainer>
		</Layout>
	);
};

export default CustomersPage;
